import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  FileScan,
  X,
  CheckCircle,
  AlertCircle,
  User,
  Building2,
  Pill,
  Calendar,
  MapPin,
  Phone,
  CreditCard,
  Stethoscope,
  Plus
} from 'lucide-react';

export const ResultsPanel = ({ isOpen, onClose, result, onCreateOrder, selectedFile }) => {
  const { t } = useTranslation('ocr');
  
  if (!isOpen) return null;

  const data = result?.data || result?.extracted_data;
  const hasRawResponse = data?.raw_response;
  
  // Try to parse raw_response if it contains JSON
  let parsedRawData = null;
  if (hasRawResponse && !data?.patient) {
    try {
      const rawStr = data.raw_response;
      const jsonMatch = rawStr.match(/\{[\s\S]*?"patient"[\s\S]*?\}/);
      if (jsonMatch) {
        let braceCount = 0;
        let startIdx = rawStr.indexOf('{');
        let endIdx = startIdx;
        
        for (let i = startIdx; i < rawStr.length; i++) {
          if (rawStr[i] === '{') braceCount++;
          if (rawStr[i] === '}') braceCount--;
          if (braceCount === 0) {
            endIdx = i + 1;
            break;
          }
        }
        
        const jsonStr = rawStr.substring(startIdx, endIdx);
        parsedRawData = JSON.parse(jsonStr);
      }
    } catch (e) {
      console.log('Could not parse raw response:', e);
    }
  }
  
  const displayData = data?.patient ? data : parsedRawData;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-background shadow-2xl z-50 transform transition-transform duration-300 ease-out">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-[#E63946] to-[#d32836] text-white">
          <div className="flex items-center gap-3">
            <FileScan className="h-6 w-6" />
            <div>
              <h2 className="font-bold text-lg">{t('results.title')}</h2>
              <p className="text-sm opacity-90">{selectedFile?.name || t('results.processedDocument')}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="text-white hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <ScrollArea className="h-[calc(100vh-140px)]">
          <div className="p-4 space-y-4">
            {result?.error ? (
              <div className="text-center py-12">
                <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
                <p className="text-lg font-medium text-foreground">{t('results.error')}</p>
                <p className="text-sm text-muted-foreground mt-2">{result.error}</p>
              </div>
            ) : !displayData ? (
              <div className="text-center py-12">
                <AlertCircle className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
                <p className="text-lg font-medium text-foreground">{t('results.noStructuredData')}</p>
                {data?.raw_response && (
                  <div className="mt-4 p-4 bg-muted rounded-lg text-left max-h-96 overflow-auto">
                    <p className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                      {data.raw_response}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Success Banner */}
                <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800 dark:text-green-200">{t('results.success')}</span>
                  </div>
                  {displayData.confidence_score && (
                    <Badge className={`${
                      displayData.confidence_score >= 80 ? 'bg-green-500' :
                      displayData.confidence_score >= 50 ? 'bg-yellow-500' :
                      'bg-red-500'
                    } text-white`}>
                      {displayData.confidence_score}% {t('results.confidence')}
                    </Badge>
                  )}
                </div>

                {/* Patient Section */}
                {displayData.patient && (
                  <div className="bg-card border rounded-lg overflow-hidden">
                    <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2 border-b flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-blue-800 dark:text-blue-200">{t('results.patient.title')}</span>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-muted-foreground">{t('results.patient.fullName')}</p>
                          <p className="font-medium">{displayData.patient.first_name} {displayData.patient.last_name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('results.patient.document')}</p>
                          <p className="font-medium flex items-center gap-1">
                            <CreditCard className="h-3 w-3" />
                            {displayData.patient.document_type}: {displayData.patient.document_number}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('results.patient.birthDate')}</p>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {displayData.patient.birth_date || t('results.patient.notSpecified')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('results.patient.phone')}</p>
                          <p className="font-medium flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {displayData.patient.phone || t('results.patient.notSpecified')}
                          </p>
                        </div>
                      </div>
                      {(displayData.patient.address || displayData.patient.city) && (
                        <div>
                          <p className="text-xs text-muted-foreground">{t('results.patient.address')}</p>
                          <p className="font-medium flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {displayData.patient.address}{displayData.patient.city && `, ${displayData.patient.city}`}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Insurance Section */}
                {displayData.insurance && (displayData.insurance.provider || displayData.insurance.policy_number) && (
                  <div className="bg-card border rounded-lg overflow-hidden">
                    <div className="bg-purple-50 dark:bg-purple-900/20 px-4 py-2 border-b flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-purple-600" />
                      <span className="font-medium text-purple-800 dark:text-purple-200">{t('results.insurance.title')}</span>
                    </div>
                    <div className="p-4 grid grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs text-muted-foreground">{t('results.insurance.provider')}</p>
                        <p className="font-medium">{displayData.insurance.provider || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('results.insurance.policyNumber')}</p>
                        <p className="font-medium">{displayData.insurance.policy_number || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('results.insurance.authorization')}</p>
                        <p className="font-medium">{displayData.insurance.authorization_number || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Order/Services Section */}
                {displayData.order && (
                  <div className="bg-card border rounded-lg overflow-hidden">
                    <div className="bg-orange-50 dark:bg-orange-900/20 px-4 py-2 border-b flex items-center gap-2">
                      <Stethoscope className="h-4 w-4 text-orange-600" />
                      <span className="font-medium text-orange-800 dark:text-orange-200">{t('results.order.title')}</span>
                    </div>
                    <div className="p-4 space-y-3">
                      {displayData.order.diagnosis && (
                        <div>
                          <p className="text-xs text-muted-foreground">{t('results.order.diagnosis')}</p>
                          <p className="font-medium">{displayData.order.diagnosis}</p>
                          {displayData.order.diagnosis_code && (
                            <Badge variant="outline" className="mt-1">{displayData.order.diagnosis_code}</Badge>
                          )}
                        </div>
                      )}
                      
                      {displayData.order.priority && (
                        <div>
                          <p className="text-xs text-muted-foreground">{t('results.order.priority')}</p>
                          <Badge className={
                            displayData.order.priority === 'urgent' ? 'bg-red-500' :
                            displayData.order.priority === 'high' ? 'bg-orange-500' :
                            displayData.order.priority === 'normal' ? 'bg-blue-500' :
                            'bg-gray-500'
                          }>
                            {displayData.order.priority}
                          </Badge>
                        </div>
                      )}

                      {displayData.order.services?.length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-2">{t('results.order.services')} ({displayData.order.services.length})</p>
                          <div className="space-y-2">
                            {displayData.order.services.map((svc, idx) => (
                              <div key={idx} className="p-2 bg-muted rounded border text-sm">
                                <p className="font-medium">{svc.description || svc.service_type}</p>
                                <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                                  {svc.frequency && <span>{t('results.order.frequency')}: {svc.frequency}</span>}
                                  {svc.duration_days && <span>{t('results.order.duration')}: {svc.duration_days} {t('results.order.days')}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {displayData.order.observations && (
                        <div>
                          <p className="text-xs text-muted-foreground">{t('results.order.observations')}</p>
                          <p className="text-sm text-foreground">{displayData.order.observations}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Prescriptions Section */}
                {displayData.prescriptions?.length > 0 && (
                  <div className="bg-card border rounded-lg overflow-hidden">
                    <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 border-b flex items-center gap-2">
                      <Pill className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-800 dark:text-green-200">{t('results.medications.title')} ({displayData.prescriptions.length})</span>
                    </div>
                    <div className="p-4 space-y-2">
                      {displayData.prescriptions.map((med, idx) => (
                        <div key={idx} className="p-3 bg-muted rounded border">
                          <p className="font-medium">{med.medication_name}</p>
                          <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-muted-foreground">
                            <span>{t('results.medications.dose')}: {med.dose || '-'}</span>
                            <span>{t('results.medications.route')}: {med.route || '-'}</span>
                            <span>{t('results.medications.frequency')}: {med.frequency || '-'}</span>
                            <span>{t('results.medications.duration')}: {med.duration_days ? `${med.duration_days} ${t('results.order.days')}` : '-'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Extraction Notes */}
                {displayData.extraction_notes && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{displayData.extraction_notes}</span>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>

        {/* Footer Actions */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-background border-t">
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={onClose}
            >
              {t('results.close')}
            </Button>
            {displayData && (
              <Button 
                className="flex-1 bg-[#E63946] hover:bg-[#d32836]"
                onClick={onCreateOrder}
                data-testid="create-order-from-ocr-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('results.createOrder')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResultsPanel;
